import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as combinedTransactionService from "@/shared/api/services/combinedTransactionService";
import * as resellerService from "@/shared/api/services/resellerService";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Search, Calendar as CalendarIcon, ArrowDownToLine, ArrowUpFromLine,
  Download, X, Activity, SlidersHorizontal, ChevronDown,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import StatusBadge from "@/shared/components/merchant/StatusBadge";

const STATUS_CONFIG = {
  SUCCESS:    { color: "#22c55e", bg: "rgba(34,197,94,0.12)",  border: "rgba(34,197,94,0.25)" },
  PENDING:    { color: "#f59e0b", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.25)" },
  PROCESSING: { color: "#06b6d4", bg: "rgba(6,182,212,0.12)",  border: "rgba(6,182,212,0.25)" },
  FAILED:     { color: "#f43f5e", bg: "rgba(244,63,94,0.12)",  border: "rgba(244,63,94,0.25)" },
  CANCELLED:  { color: "#94a3b8", bg: "rgba(148,163,184,0.10)", border: "rgba(148,163,184,0.2)" },
  EXPIRED:    { color: "#78716c", bg: "rgba(120,113,108,0.10)", border: "rgba(120,113,108,0.2)" },
};

function InlineBadge({ status }) {
  const cfg = STATUS_CONFIG[String(status).toUpperCase()] || STATUS_CONFIG.PENDING;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.color }} />
      {String(status).charAt(0) + String(status).slice(1).toLowerCase()}
    </span>
  );
}

export default function ResellerTransactions() {
  const { t, language } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedTx, setSelectedTx] = useState(null);
  const resellerName = "Asia Gaming Network";

  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["reseller-combined-transactions"],
    queryFn: () => combinedTransactionService.getResellerCombinedTransactions(),
  });
  const { data: merchants = [] } = useQuery({
    queryKey: ["reseller-merchants-list"],
    queryFn: () => resellerService.getMyMerchants(),
  });

  const filteredTransactions = transactions.filter(tx => {
    const ns = String(tx.status || "").toUpperCase();
    const nt = String(tx.type || "").toUpperCase();
    if (search && !tx.transaction_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && ns !== statusFilter) return false;
    if (typeFilter !== "all" && nt !== typeFilter) return false;
    if (merchantFilter !== "all" && tx.merchant_id !== merchantFilter) return false;
    if (dateRange.from && new Date(tx.created_date) < dateRange.from) return false;
    if (dateRange.to && new Date(tx.created_date) > dateRange.to) return false;
    return true;
  });

  const clearFilters = () => {
    setSearch(""); setStatusFilter("all"); setTypeFilter("all");
    setMerchantFilter("all"); setDateRange({ from: null, to: null });
  };

  const hasFilters = search || statusFilter !== "all" || typeFilter !== "all" || merchantFilter !== "all" || dateRange.from;

  const totalSuccess = filteredTransactions.filter(t => String(t.status).toUpperCase() === "SUCCESS").length;
  const totalAmt = filteredTransactions.reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <ResellerLayout currentPage="ResellerTransactions" resellerName={resellerName}>
      <style>{`
        .tx-bg { min-height: 100vh; background: #060f1c; scroll-behavior: smooth; }
        .glass { background: rgba(13,27,46,0.75); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.065); }
        .row-hover { transition: background 0.18s, transform 0.18s; }
        .row-hover:hover { background: rgba(6,182,212,0.04); transform: translateX(2px); }
        .scrollbar-thin::-webkit-scrollbar { width: 4px; height: 4px; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(6,182,212,0.25); border-radius: 99px; }
        .filter-select .select-trigger { background: rgba(13,27,46,0.9) !important; border-color: rgba(255,255,255,0.07) !important; color: #94a3b8 !important; }
      `}</style>

      <div className="tx-bg space-y-6 p-1 sm:p-0">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Activity className="h-4 w-4 text-cyan-400" />
              <span className="text-xs tracking-[0.2em] uppercase text-cyan-400/70 font-semibold">All Transactions</span>
            </div>
            <h1 className="text-3xl font-black text-white">{t("transactions")}</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-slate-400">{filteredTransactions.length} {t("transactions")}</span>
              <span className="w-1 h-1 rounded-full bg-slate-600" />
              <span className="text-sm text-emerald-400 font-semibold">{totalSuccess} success</span>
              <span className="w-1 h-1 rounded-full bg-slate-600" />
              <span className="text-sm text-cyan-400 font-semibold">฿{totalAmt.toLocaleString()}</span>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl glass text-slate-300 hover:text-cyan-400 text-sm font-semibold transition-colors border border-white/[0.07]"
          >
            <Download className="h-4 w-4" />
            {t("export")}
          </motion.button>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="glass rounded-2xl p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <SlidersHorizontal className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Filters</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                placeholder={`${t("search")}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm text-slate-200 placeholder-slate-500 outline-none bg-[#0a1929] border border-white/[0.07] focus:border-cyan-500/40 transition-colors"
              />
            </div>
            {/* Merchant */}
            <Select value={merchantFilter} onValueChange={setMerchantFilter}>
              <SelectTrigger className="w-[140px] bg-[#0a1929] border-white/[0.07] text-slate-300 rounded-xl">
                <SelectValue placeholder={t("merchant")} />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1b2e] border-white/[0.07] text-slate-200">
                <SelectItem value="all">{t("allMerchants")}</SelectItem>
                {merchants.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Status */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] bg-[#0a1929] border-white/[0.07] text-slate-300 rounded-xl">
                <SelectValue placeholder={t("status")} />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1b2e] border-white/[0.07] text-slate-200">
                <SelectItem value="all">{t("allStatus")}</SelectItem>
                {["PENDING","PROCESSING","SUCCESS","FAILED","CANCELLED","EXPIRED"].map(s => (
                  <SelectItem key={s} value={s}>{s.charAt(0)+s.slice(1).toLowerCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Type */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[120px] bg-[#0a1929] border-white/[0.07] text-slate-300 rounded-xl">
                <SelectValue placeholder={t("type")} />
              </SelectTrigger>
              <SelectContent className="bg-[#0d1b2e] border-white/[0.07] text-slate-200">
                <SelectItem value="all">{t("allTypes")}</SelectItem>
                <SelectItem value="DEPOSIT">{t("deposit")}</SelectItem>
                <SelectItem value="WITHDRAWAL">{t("withdrawal")}</SelectItem>
              </SelectContent>
            </Select>
            {/* Date */}
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#0a1929] border border-white/[0.07] text-slate-300 text-sm hover:border-cyan-500/30 transition-colors">
                  <CalendarIcon className="h-4 w-4 text-slate-500" />
                  <span className="hidden sm:inline text-sm">
                    {dateRange.from ? format(dateRange.from, "MMM d") : t("dateRange")}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#0d1b2e] border-white/[0.07]" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(r) => setDateRange(r || { from: null, to: null })}
                  numberOfMonths={2}
                  className="text-slate-200"
                />
              </PopoverContent>
            </Popover>
            {/* Clear */}
            <AnimatePresence>
              {hasFilters && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={clearFilters}
                  whileHover={{ scale: 1.1 }}
                  className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/20 transition-colors"
                >
                  <X className="h-4 w-4" />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-2xl overflow-hidden"
        >
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {[t("transaction"), t("merchant"), t("amount"), t("status"), t("date")].map((h, i) => (
                    <th
                      key={h}
                      className={`px-5 py-4 text-left text-[10px] font-bold text-slate-500 uppercase tracking-[0.15em] ${i === 2 ? "text-right" : ""} ${i === 1 ? "hidden sm:table-cell" : ""} ${i === 4 ? "hidden md:table-cell" : ""}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ repeat: Infinity, duration: 1.2, ease: "linear" }}
                          className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full"
                        />
                        <span className="text-sm text-slate-500">{t("loading")}</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-slate-800/60 flex items-center justify-center">
                          <Activity className="h-7 w-7 text-slate-600" />
                        </div>
                        <p className="text-slate-500 text-sm">{t("noTransactionsFound")}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.slice(0, 50).map((tx, i) => (
                    <motion.tr
                      key={tx.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.018, duration: 0.3 }}
                      onClick={() => setSelectedTx(tx)}
                      className="row-hover border-b border-white/[0.04] cursor-pointer"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <motion.div
                            whileHover={{ rotate: 8, scale: 1.12 }}
                            className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              String(tx.type).toUpperCase() === "DEPOSIT"
                                ? "bg-cyan-500/12 border border-cyan-500/20"
                                : "bg-emerald-500/12 border border-emerald-500/20"
                            }`}
                          >
                            {String(tx.type).toUpperCase() === "DEPOSIT" ? (
                              <ArrowDownToLine className="h-4 w-4 text-cyan-400" />
                            ) : (
                              <ArrowUpFromLine className="h-4 w-4 text-emerald-400" />
                            )}
                          </motion.div>
                          <div>
                            <p className="font-mono text-sm text-slate-200">{tx.transaction_id?.slice(0, 12)}…</p>
                            <p className="text-xs text-slate-500 sm:hidden">{tx.merchant_name || t("nA")}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden sm:table-cell text-sm text-slate-300">{tx.merchant_name || t("nA")}</td>
                      <td className="px-5 py-3.5 text-right">
                        <span className={`text-sm font-black ${String(tx.type).toUpperCase() === "DEPOSIT" ? "text-cyan-400" : "text-emerald-400"}`}>
                          ฿{tx.amount?.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <InlineBadge status={tx.status} />
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell text-xs text-slate-500">
                        {tx.created_date ? format(new Date(tx.created_date), language === "th" ? "d MMM, HH:mm" : "MMM d, HH:mm", { locale: language === "th" ? th : enUS }) : t("nA")}
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {filteredTransactions.length > 0 && (
            <div className="px-5 py-3 border-t border-white/[0.05] flex items-center justify-between">
              <span className="text-xs text-slate-500">
                Showing {Math.min(50, filteredTransactions.length)} of {filteredTransactions.length}
              </span>
              <div className="w-24 h-1 rounded-full bg-white/[0.05] overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-indigo-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min((50 / filteredTransactions.length) * 100, 100)}%` }}
                  transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }}
                />
              </div>
            </div>
          )}
        </motion.div>

        <TransactionDetailPanel
          transaction={selectedTx}
          open={!!selectedTx}
          onClose={() => setSelectedTx(null)}
        />
      </div>
    </ResellerLayout>
  );
}